import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sun, Moon, Scroll, Globe, Download, Upload, Trash, Palette } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';
import { ThemeCustomizer } from './ThemeCustomizer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

export const SettingsSheet = ({ isOpen, onClose, onDeleteCharacter, character }) => {
  const { state, dispatch, exportBackup, importBackup } = useApp();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  
  const themes = [
    { id: 'light', label: t('themeLight'), icon: Sun },
    { id: 'dark', label: t('themeDark'), icon: Moon },
    { id: 'parchment', label: t('themeParchment'), icon: Scroll },
    { id: 'custom', label: state.language === 'it' ? 'Personalizzato' : 'Custom', icon: Palette },
  ];
  
  const languages = [
    { id: 'it', label: 'Italiano', flag: '🇮🇹' },
    { id: 'en', label: 'English', flag: '🇬🇧' },
  ];
  
  const handleThemeChange = (themeId) => {
    if (themeId === 'custom') {
      setShowCustomizer(true);
    } else {
      dispatch({ type: 'SET_THEME', payload: themeId });
    }
  };
  
  const handleLanguageChange = (langId) => {
    dispatch({ type: 'SET_LANGUAGE', payload: langId });
  };
  
  const handleBackup = () => {
    exportBackup();
    toast.success(t('backupSuccess'));
  };
  
  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      await importBackup(file);
      toast.success(t('restoreSuccess'));
    } catch (err) {
      toast.error(t('invalidBackupFile'));
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          {/* Sheet */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-3xl p-6 max-w-md mx-auto max-h-[80vh] overflow-y-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[var(--text)]">{t('settings')}</h2>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-[var(--background)] transition-colors"
                data-testid="close-settings"
              >
                <X size={24} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            {/* Theme */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-3">
                {t('theme')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {themes.map(theme => {
                  const Icon = theme.icon;
                  const isActive = state.theme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
                        isActive
                          ? 'bg-[var(--primary)] text-white'
                          : 'bg-[var(--background)] text-[var(--text)] hover:bg-[var(--background)]/80'
                      }`}
                      data-testid={`theme-${theme.id}`}
                    >
                      <Icon size={24} weight="fill" />
                      <span className="text-xs font-medium">{theme.label}</span>
                    </button>
                  );
                })}
              </div>
              {state.theme === 'custom' && (
                <button
                  onClick={() => setShowCustomizer(true)}
                  className="w-full mt-2 p-2 text-sm text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-xl transition-colors"
                  data-testid="edit-custom-theme"
                >
                  {state.language === 'it' ? 'Modifica tema personalizzato' : 'Edit custom theme'}
                </button>
              )}
            </div>
            
            {/* Language */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-3">
                {t('language')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {languages.map(lang => (
                  <button
                    key={lang.id}
                    onClick={() => handleLanguageChange(lang.id)}
                    className={`flex items-center justify-center gap-2 p-3 rounded-2xl transition-all ${
                      state.language === lang.id
                        ? 'bg-[var(--primary)] text-white'
                        : 'bg-[var(--background)] text-[var(--text)] hover:bg-[var(--background)]/80'
                    }`}
                    data-testid={`language-${lang.id}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Backup & Restore */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-3">
                {t('backup')} / {t('restore')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={handleBackup}
                  className="flex items-center justify-center gap-2 h-12 rounded-2xl bg-[var(--background)] text-[var(--text)] hover:bg-[var(--background)]/80"
                  variant="ghost"
                  data-testid="backup-btn"
                >
                  <Download size={20} />
                  {t('backup')}
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 h-12 rounded-2xl bg-[var(--background)] text-[var(--text)] hover:bg-[var(--background)]/80"
                  variant="ghost"
                  data-testid="restore-btn"
                >
                  <Upload size={20} />
                  {t('restore')}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleRestore}
                  className="hidden"
                  data-testid="restore-file-input"
                />
              </div>
            </div>

            {/* Legal */}
            <div className="mb-6 pt-2 border-t border-[var(--border)]">
              <Link
                to="/privacy"
                onClick={onClose}
                className="flex items-center justify-center w-full h-11 rounded-2xl text-sm font-medium text-[var(--primary)] hover:bg-[var(--primary)]/10 transition-colors"
              >
                {state.language === 'en' ? 'Privacy Policy' : 'Informativa privacy'}
              </Link>
            </div>
            
            {/* Delete Character */}
            {character && (
              <div className="pt-4 border-t border-[var(--border)]">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full flex items-center justify-center gap-2 h-12 rounded-2xl text-red-500 hover:bg-red-500/10"
                      data-testid="delete-character-btn"
                    >
                      <Trash size={20} />
                      {t('deleteCharacter')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[var(--surface)] border-[var(--border)]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-[var(--text)]">{t('deleteCharacter')}</AlertDialogTitle>
                      <AlertDialogDescription className="text-[var(--text-muted)]">
                        {t('deleteCharacterConfirm')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[var(--background)] text-[var(--text)] border-[var(--border)]">
                        {t('cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={onDeleteCharacter}
                        className="bg-red-500 text-white hover:bg-red-600"
                        data-testid="confirm-delete-character"
                      >
                        {t('delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </motion.div>
        </>
      )}
      
      <ThemeCustomizer 
        isOpen={showCustomizer} 
        onClose={() => setShowCustomizer(false)} 
      />
    </AnimatePresence>
  );
};
